import { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { MenuItem } from '../../types/type';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/form/Button';
import { ConfirmationModal } from '../ui/feedback/ConfirmationModal';
import { useToast } from '@/context/ToastContext';

interface MainSidebarProps {
  menu: MenuItem[];
  collapsed: boolean;
  toggleCollapse: () => void;
}

export const MainSidebar = ({ menu, collapsed, toggleCollapse }: MainSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState<string>(
    () => localStorage.getItem('activeItem') || 'Dashboard',
  );
  const [expandedItem, setExpandedItem] = useState<string | null>(() =>
    localStorage.getItem('expandedItem'),
  );
  const [activeSubItem, setActiveSubItem] = useState<string | null>(() =>
    localStorage.getItem('activeSubItem'),
  );

  const { showToast } = useToast();

  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean }>({
    isOpen: false,
  });

  const handleLogOut = () => {
    try {
      localStorage.clear();
      showToast({ message: 'Logged out successfully', type: 'success' });
      navigate('/');
    } catch (err: any) {
      showToast({ message: err || 'Failed to logout', type: 'error' });
    } finally {
      setDeleteDialog({ isOpen: false });
    }
  };

  const handleMenuClick = (item: MenuItem) => {
    setActiveItem(item.name);
    localStorage.setItem('activeItem', item.name);
    if (item.submenu) {
      const newExpandedItem = expandedItem === item.name ? null : item.name;
      setExpandedItem(newExpandedItem);
      newExpandedItem
        ? localStorage.setItem('expandedItem', newExpandedItem)
        : localStorage.removeItem('expandedItem');
      navigate(item.route);
    } else {
      setExpandedItem(null);
      localStorage.removeItem('expandedItem');
      navigate(item.route);
    }
  };

  const handleSubMenuClick = (subItem: MenuItem) => {
    setActiveSubItem(subItem.name);
    localStorage.setItem('activeSubItem', subItem.name);
    navigate(subItem.route);
  };

  useEffect(() => {
    if (!isExpanded) {
      setExpandedItem(null);
      localStorage.removeItem('expandedItem');
    }
  }, [isExpanded]);

  useEffect(() => {
    if (collapsed) {
      setExpandedItem(null);
      localStorage.removeItem('expandedItem');
    }
  }, [collapsed]);

  useEffect(() => {
    let found = false;

    menu.forEach((item) => {
      if (location.pathname.startsWith(item.route)) {
        setActiveItem(item.name);
        localStorage.setItem('activeItem', item.name);
        found = true;
      }

      item.submenu?.forEach((sub) => {
        if (location.pathname.startsWith(sub.route)) {
          setActiveItem(item.name);
          setExpandedItem(item.name);
          setActiveSubItem(sub.name);

          localStorage.setItem('activeItem', item.name);
          localStorage.setItem('expandedItem', item.name);
          localStorage.setItem('activeSubItem', sub.name);

          found = true;
        }
      });
    });
  }, [location.pathname, menu]);

  return (
    <div
      className={`${collapsed ? 'w-20' : 'w-64'} h-screen transition-all duration-300 bg-white relative shadow-md flex flex-col`}
    >
      {/* Toggle Button */}
      <button
        onClick={toggleCollapse}
        className='absolute -right-3 top-[68px] rounded-full p-1 z-50 shadow-md bg-green-900 text-white hover:scale-105 transition-transform'
      >
        <Icons.ChevronRight
          size={18}
          className={`${collapsed ? '' : 'rotate-180'} transition-transform`}
        />
      </button>

      {/* Header */}
      <div className=' text-center border-b border-gray-200 '>
        {collapsed ? (
          <img src='/images/logo.png' alt='' className='h-20 w-full object-full' />
        ) : (
          <img src='/images/text-logo-2 1.png' className='h-20 w-full p-3' />
        )}
      </div>

      {/* Navigation */}
      <nav className='mt-4 px-2 overflow-y-auto scrollbar-hidden flex-1'>
        {menu.map((item) => {
          const isActive = activeItem === item.name;
          const IconComponent = item.icon;
          const isExpandedItem = expandedItem === item.name;
          return (
            <div key={item.name}>
              <button
                onClick={() => handleMenuClick(item)}
                className={`w-full flex items-center group px-3 py-2 rounded-lg transition-all duration-200 
                   ${isActive ? 'bg-emerald-600 shadow text-white font-semibold' : 'hover:bg-white/30 text-green-900'}`}
              >
                <div className={`flex items-center gap-3 ${!isExpanded ? 'mx-auto' : ''}`}>
                  <div
                    className={`p-1.5 rounded-md ${isActive ? 'bg-white text-emerald-600' : 'group-hover:bg-green-100'} transition`}
                  >
                    <IconComponent />
                  </div>
                  {!collapsed && <span className='text-sm'>{item.name}</span>}
                </div>
                {!collapsed && item.submenu?.length !== 0 && (
                  <Icons.ChevronDown
                    size={14}
                    className={`ml-auto transition-transform ${isExpandedItem ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {/* Submenu Items */}
              {item.submenu && isExpandedItem && (
                <div className={`${collapsed ? 'ml-3' : 'ml-6'} mt-3 space-y-2 `}>
                  {item.submenu.map((subItem) => {
                    const isSubActive = activeSubItem === subItem.name;

                    return (
                      <button
                        key={subItem.name}
                        onClick={() => handleSubMenuClick(subItem)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition
                          ${
                            isSubActive
                              ? 'bg-green-200  text-green-900'
                              : 'bg-green-50 text-green-900 hover:bg-green-100'
                          }`}
                      >
                        {subItem.icon && <subItem.icon />}
                        {!collapsed && <span>{subItem.name}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className='p-4'>
        <Button
          variant='danger'
          className='w-full flex items-center justify-center gap-2'
          onClick={() => setDeleteDialog({ isOpen: true })}
        >
          <Icons.LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </Button>

        <ConfirmationModal
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false })}
          onConfirm={() => handleLogOut()}
          title='Confirm Logout'
          message='Are you sure you want to log out? This action cannot be undone.'
          confirmText='Logout'
          cancelText='Cancel'
          variant='secondary'
        />
      </div>
    </div>
  );
};
